<template>
  <header class="ap-topbar">
    <div class="ap-topbar-inner">

      <div class="ap-brand" @click="$router.push({ name: 'Admin-Panel' }).catch(() => {})">
        <img src="@/assets/logo_text.png" alt="Tyng &amp; Co" class="ap-brand-logo" />
        <app-divider class="ap-brand-divider" width="3px" height="22px" color="#d1d5db"></app-divider>
        <span class="ap-brand-console">Admin Console</span>
      </div>

      <app-divider class="ap-brand-divider" width="3px" height="22px" color="#d1d5db"></app-divider>

      <nav class="ap-crumbs">
        <router-link :to="{ name: 'Admin-Panel', params: { lang: $route.params.lang || $i18n.locale } }" class="ap-crumb-link">Dashboard</router-link>
        <template v-for="(crumb, i) in crumbs">
          <fa-icon :key="'sep' + i" :icon="['fas', 'chevron-right']" class="ap-crumb-sep" />
          <router-link v-if="crumb.to" :key="'link' + i" :to="crumb.to" class="ap-crumb-link">{{ crumb.label }}</router-link>
          <span v-else :key="'active' + i" class="ap-crumb-active">{{ crumb.label }}</span>
        </template>
      </nav>

      <div class="ap-topbar-actions">

        <!-- Full variant: system status pill + session timer -->
        <template v-if="variant === 'full'">
          <span class="ap-pill"><span class="ap-dot"></span> System healthy</span>
          <span
            v-if="showTimer"
            class="ap-pill ap-timer"
            :class="{ 'ap-timer-warn': sessionCountdown > 0 && sessionCountdown <= 300 }"
          >
            <fa-icon :icon="['fas', 'clock']" />
            {{ formattedCountdown }}
          </span>
        </template>

        <!-- Back button — shown on back-variant pages, any page that provides backTo, or showBack=true -->
        <button v-if="variant === 'back' || backTo || showBack" class="ap-iconbackbtn" title="Back" @click="handleBack">
            <fa-icon :icon="['fas', 'arrow-left']" />
        </button>
        <app-divider v-if="variant === 'back' || backTo || showBack" width="1px" height="22px" color="#909495" class="mx-1"></app-divider>

        <!-- Avatar with dropdown — settings + logout, always visible -->
        <div class="ap-avatar-wrap" ref="avatarWrap">
          <div class="ap-avatar" :title="avatarInitials === 'SA' ? 'Super Admin' : 'Admin'" @click="toggleAvatarMenu">{{ avatarInitials }}</div>
          <transition name="ap-avatar-menu-fade">
            <div v-if="avatarMenuOpen" class="ap-avatar-menu">
              <button v-if="avatarInitials === 'SA'" class="ap-avatar-menu-item" @click="openSettings">
                <fa-icon :icon="['fas', 'cog']" />
                Settings
              </button>
              <div v-if="avatarInitials === 'SA'" class="ap-avatar-menu-divider"></div>
              <button class="ap-avatar-menu-item ap-avatar-menu-item--danger" @click="handleLogout">
                <fa-icon :icon="['fas', 'sign-out-alt']" />
                Logout
              </button>
            </div>
          </transition>
        </div>

      </div>
    </div>

    <!-- Settings modal — always available from any admin page -->
    <admin-options-modal
      :show="showSettingsModal"
      :branches="irbBranches"
      :auditors="auditors"
      :categories="contactCategories"
      :titles="contactTitles"
      @close="showSettingsModal = false"
      @save="handleSettingsSave"
    />

  </header>
</template>

<script>

import AppDivider from '../shared/AppDivider.vue'
import AdminOptionsModal from './AdminOptionsModal.vue'

export default {
  name: 'AdminHeader',
  components: { AppDivider, AdminOptionsModal },
  props: {
    crumbs: { type: Array, default: () => [] },
    variant: {
      type: String,
      default: 'back',
      validator: v => ['full', 'back'].includes(v)
    },
    showTimer: { type: Boolean, default: true },
    sessionCountdown: { type: Number, default: 0 },
    formattedCountdown: { type: String, default: '' },
    backTo: { type: [Object, String], default: null },
    showBack: { type: Boolean, default: false }
  },
  computed: {
    avatarInitials () {
      return localStorage.getItem('adminRole') === 'super_admin' ? 'SA' : 'AD'
    }
  },
  data () {
    return {
      avatarMenuOpen: false,
      showSettingsModal: false,
      irbBranches: ['Cheras', 'W.P. Kuala Lumpur', 'Bukit Mertajam', 'Petaling Jaya', 'Kuala Lumpur Bandar'],
      auditors: ['Marie J & Co', 'Cheng & Co', 'YYC PLT'],
      contactCategories: ['Internal Staff', 'Outsourced Account', 'Director', 'Shareholder'],
      contactTitles: ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', "Dato'", 'Datin', 'Tan Sri', 'Puan Sri']
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
    this.fetchAdminSettings()
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    handleBack () {
      if (this.backTo) {
        // Explicit destination provided — use it (e.g. TaskTax → Dashboard)
        this.$router.push(this.backTo).catch(() => {})
      } else if (window.history.length > 1) {
        // Follow actual browser history (e.g. EditClient → wherever it was opened from)
        this.$router.back()
      } else {
        // No history (direct link / new tab) — fall back to dashboard
        this.$router.push({ name: 'Admin-Panel', params: { lang: this.$route.params.lang || this.$i18n.locale } }).catch(() => {})
      }
    },
    toggleAvatarMenu () {
      this.avatarMenuOpen = !this.avatarMenuOpen
    },
    handleOutsideClick (e) {
      if (this.$refs.avatarWrap && !this.$refs.avatarWrap.contains(e.target)) {
        this.avatarMenuOpen = false
      }
    },
    handleLogout () {
      this.avatarMenuOpen = false
      this.$emit('open-logout')
    },
    openSettings () {
      this.avatarMenuOpen = false
      this.showSettingsModal = true
    },
    async fetchAdminSettings () {
      try {
        const token = localStorage.getItem('adminToken')
        if (!token) return
        const res = await fetch('https://tyng-ca.com.my/api/admin/settings/get.php', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
        if (!res.ok) return
        const data = await res.json()
        if (data && data.success && data.data) {
          if (Array.isArray(data.data.irb_branches)) this.irbBranches = data.data.irb_branches
          if (Array.isArray(data.data.auditors)) this.auditors = data.data.auditors
          if (Array.isArray(data.data.contact_categories)) this.contactCategories = data.data.contact_categories
          if (Array.isArray(data.data.contact_titles)) this.contactTitles = data.data.contact_titles
        }
      } catch (err) {
        console.error('AdminHeader: failed to load settings:', err)
      }
    },
    async handleSettingsSave ({ branches, auditors, categories, titles }) {
      this.irbBranches = branches
      this.auditors = auditors
      this.contactCategories = categories
      this.contactTitles = titles
      this.showSettingsModal = false
      this.$emit('settings-saved', { branches, auditors, categories, titles })
      try {
        const token = localStorage.getItem('adminToken')
        await fetch('https://tyng-ca.com.my/api/admin/settings/save.php', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ irb_branches: branches, auditors, contact_categories: categories, contact_titles: titles })
        })
      } catch (err) {
        console.error('AdminHeader: failed to save settings:', err)
      }
    }
  }
}
</script>

<style scoped>
.ap-brand-logo {
  height: 22px;
  width: auto;
  display: block;
}
.ap-brand-console {
  font-size: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.55;
  color: inherit;
}
</style>
